import { api } from "@/services/api";
import { API_ROUTES } from "@/config/api";
import { unifiedServices } from "@/services/unifiedService";
import { isSupabaseMode } from "@/config/dataSource";
import type { Cliente, ClienteStatus, DocumentoCliente } from "../types/cliente";

export interface ListarClientesResponse {
  data: Cliente[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

interface APICliente {
  id: string;
  nome: string;
  email: string;
  cpf: string;
  telefone?: string; // compatibilidade com API antiga
  tel1?: string;
  tel2?: string;
  data_nascimento?: string;
  rg?: string;
  data_emissao_rg?: string;
  naturalidade?: string;
  nacionalidade?: string;
  atividade_desenvolvida?: string;
  rendimento_titular?: string;
  nome_mae?: string;
  nome_pai?: string;
  estado_civil?: string;
  nome_conjuge?: string;
  cpf_conjuge?: string;
  data_nascimento_conjuge?: string;
  banco?: string;
  agencia?: string;
  conta?: string;
  tipo_conta?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cep?: string;
  cidade?: string;
  estado?: string;
  documento_com_foto_doc?: string;
  residencia_doc?: string;
  dataEntrada?: string; // compatibilidade com API antiga
  created_at?: string;
  updated_at?: string;
  status: string;
  tenant_id: number;
  grupo?: string;
  parcelas_pagas?: number;
  valor_contrato?: string;
  contratos?: any[];
  tenant?: {
    id: string;
    nome: string;
  };
}

interface APIClientesResponse {
  data: APICliente[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// Mapeamento de nomes de estados para siglas
const estadoNomeParaSigla: { [key: string]: string } = {
  'Acre': 'AC',
  'Alagoas': 'AL',
  'Amapá': 'AP',
  'Amazonas': 'AM',
  'Bahia': 'BA',
  'Ceará': 'CE',
  'Distrito Federal': 'DF',
  'Espírito Santo': 'ES',
  'Goiás': 'GO',
  'Maranhão': 'MA',
  'Mato Grosso': 'MT',
  'Mato Grosso do Sul': 'MS',
  'Minas Gerais': 'MG',
  'Pará': 'PA',
  'Paraíba': 'PB',
  'Paraná': 'PR',
  'Pernambuco': 'PE',
  'Piauí': 'PI',
  'Rio de Janeiro': 'RJ',
  'Rio Grande do Norte': 'RN',
  'Rio Grande do Sul': 'RS',
  'Rondônia': 'RO',
  'Roraima': 'RR',
  'Santa Catarina': 'SC',
  'São Paulo': 'SP',
  'Sergipe': 'SE',
  'Tocantins': 'TO'
};

const mapearCliente = (cliente: APICliente): Cliente => {
  console.log('🔍 mapearCliente - cliente.estado:', cliente.estado);
  console.log('🔍 mapearCliente - cliente completo:', cliente);
  
  // Converter nome do estado para sigla se necessário
  let estadoMapeado = cliente.estado || "";
  if (cliente.estado && estadoNomeParaSigla[cliente.estado]) {
    estadoMapeado = estadoNomeParaSigla[cliente.estado];
    console.log('🔍 Estado convertido:', cliente.estado, '->', estadoMapeado);
  }
  
  return {
    id: cliente.id,
    nome: cliente.nome,
    email: cliente.email,
    cpf: cliente.cpf,
    tel1: cliente.tel1 || cliente.telefone || "", // compatibilidade com API antiga
    tel2: cliente.tel2 || "",
    data_nascimento: cliente.data_nascimento || null,
    rg: cliente.rg || "",
    data_emissao_rg: cliente.data_emissao_rg || null,
    naturalidade: cliente.naturalidade || "",
    nacionalidade: cliente.nacionalidade || "",
    atividade_desenvolvida: cliente.atividade_desenvolvida || "",
    rendimento_titular: cliente.rendimento_titular || "",
    nome_mae: cliente.nome_mae || "",
    nome_pai: cliente.nome_pai || "",
    estado_civil: (cliente.estado_civil as any) || 'solteiro',
    nome_conjuge: cliente.nome_conjuge || "",
    cpf_conjuge: cliente.cpf_conjuge || "",
    data_nascimento_conjuge: cliente.data_nascimento_conjuge || null,
    banco: cliente.banco || "",
    agencia: cliente.agencia || "",
    conta: cliente.conta || "",
    tipo_conta: (cliente.tipo_conta as any) || undefined,
    logradouro: cliente.logradouro || "",
    numero: cliente.numero || "",
    complemento: cliente.complemento || "",
    bairro: cliente.bairro || "",
    cep: cliente.cep || "",
    cidade: cliente.cidade || "",
    estado: estadoMapeado,
    documento_com_foto_doc: cliente.documento_com_foto_doc,
    residencia_doc: cliente.residencia_doc,
    created_at: cliente.created_at || cliente.dataEntrada || "",
    tenant_id: cliente.tenant_id.toString(),
    status: cliente.status as ClienteStatus,
    tenant: cliente.tenant,
    updated_at: cliente.updated_at || cliente.dataEntrada || "",
    contratos: cliente.contratos || (cliente.grupo ? [{
      id: cliente.grupo,
      numero: cliente.grupo,
      status: cliente.status,
      created_at: cliente.created_at || cliente.dataEntrada || "",
      parcelas_pagas: cliente.parcelas_pagas || 0,
      valor_contrato: cliente.valor_contrato || "0"
    }] : [])
  };
};

export class ClientesService {
  static async listarClientes(page = 1, per_page = 10, search?: string, tenantId?: string, filterByUserId?: string): Promise<ListarClientesResponse> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.listarClientes(page, per_page, search, tenantId, filterByUserId);
    }

    const params = new URLSearchParams({
      page: page.toString(),
      limit: per_page.toString(), // Backend usa 'limit' em vez de 'per_page'
      ...(search && { search }),
    });

    console.log('🔍 ClientesService.listarClientes - Parâmetros:', { page, per_page, search });
    console.log('🔍 ClientesService.listarClientes - URL:', API_ROUTES.CLIENTES.BASE);
    console.log('🔍 ClientesService.listarClientes - Params:', params.toString());

    try {
      const response = await api.get<APIClientesResponse>(
        API_ROUTES.CLIENTES.BASE,
        { params }
      );

      console.log('🔍 ClientesService.listarClientes - Resposta completa:', response);
      console.log('🔍 ClientesService.listarClientes - Response.data:', response.data);

      // A API retorna { data: [...], total: ..., page: ..., limit: ..., total_pages: ... }
      const clientes = response.data?.data || [];
      const total = response.data?.total || 0;
      const totalPages = response.data?.total_pages || 1;

      console.log('🔍 ClientesService.listarClientes - Clientes extraídos:', clientes);
      console.log('🔍 ClientesService.listarClientes - Total:', total);
      console.log('🔍 ClientesService.listarClientes - Total pages:', totalPages);

      return {
        data: clientes.map(mapearCliente),
        total,
        page,
        per_page: per_page,
        total_pages: totalPages
      };
    } catch (error) {
      console.error('🔍 ClientesService.listarClientes - Erro:', error);
      throw error;
    }
  }

  static async buscarCliente(id: string): Promise<Cliente> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.findById(id);
    }
    const response = await api.get<APICliente>(API_ROUTES.CLIENTES.DETAILS(id));
    return mapearCliente(response.data);
  }

  static async criarCliente(cliente: Partial<Cliente>): Promise<Cliente> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.create(cliente);
    }
    const response = await api.post<APICliente>(API_ROUTES.CLIENTES.BASE, cliente);
    return mapearCliente(response.data);
  }

  static async atualizarCliente(id: string, cliente: Partial<Cliente>): Promise<Cliente> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.update(id, cliente);
    }
    const response = await api.put<APICliente>(API_ROUTES.CLIENTES.DETAILS(id), cliente);
    return mapearCliente(response.data);
  }

  static async excluirCliente(id: string): Promise<void> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.delete(id);
    }
    await api.delete(API_ROUTES.CLIENTES.DETAILS(id));
  }

  static async excluirClientesEmLote(ids: string[]): Promise<{ message: string; deletedCount: number; deletedIds: number[] }> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.excluirClientesEmLote(ids);
    }
    const response = await api.delete(API_ROUTES.CLIENTES.DELETE_MULTIPLE, {
      data: { ids: ids.map(id => parseInt(id)) }
    });
    return response.data;
  }

  static async uploadDocumento(clienteId: string, file: File, tipo: string, tenantId?: string, userId?: string): Promise<any> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.uploadDocumento(clienteId, file, tipo, tenantId, userId);
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);

    const response = await api.post(
      API_ROUTES.CLIENTES.DOCUMENTOS.UPLOAD(clienteId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  static async listarDocumentos(clienteId: string): Promise<any[]> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.listarDocumentos(clienteId);
    }
    const response = await api.get(API_ROUTES.CLIENTES.DOCUMENTOS.LISTAR(clienteId));
    return response.data;
  }

  static async excluirDocumento(clienteId: string, documentoId: string): Promise<void> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.excluirDocumento(clienteId, documentoId);
    }
    await api.delete(API_ROUTES.CLIENTES.DOCUMENTOS.EXCLUIR(clienteId, documentoId));
  }


  static async verificarCPFExistente(cpf: string, ignoreClienteId?: string): Promise<boolean> {
    if (isSupabaseMode()) {
      return await unifiedServices.clientes.verificarCPFExistente(cpf, ignoreClienteId);
    }
    const params = new URLSearchParams({
      cpf: cpf.replace(/\D/g, ''), // Remove formatação
      ...(ignoreClienteId && { ignore_cliente_id: ignoreClienteId })
    });

    const response = await api.get(API_ROUTES.CLIENTES.VERIFICAR_CPF, { params });
    return response.data.exists;
  }
} 